import { getUserProfile } from 'helpers/userProfile';
import { useFilterContext } from 'pages/NewOverview/contexts/FilterContext';
import { useHealthDataContext } from 'pages/NewOverview/contexts/HealthDataContext';
import { handleFilterStateRoute } from 'pages/NewOverview/helpers/filters';
import {
  ReactElement, useEffect, useMemo, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { LoadingUnitsInfo } from '~/pages/NewOverview/components/Loading/UnitsInfo';
import { HealthUnitCard } from './Card';
import { HealthUnitsInfoContainer } from './styles';
import { HealthVariant } from './types';

interface HealthUnitsInfoProps {
  loading: boolean;
  hasHealth: boolean;
}

export function HealthUnitsInfo({
  hasHealth,
  loading,
}: Readonly<HealthUnitsInfoProps>): ReactElement {
  const [profile] = useState(getUserProfile());
  const history = useHistory();
  const {
    data,
    loading: loadingHealth,
    refetch,
    units,
  } = useHealthDataContext();
  const { clients, selectedOptions } = useFilterContext();

  const generalLoading = useMemo(() => loading || (loadingHealth && hasHealth), [hasHealth, loading, loadingHealth]);

  const hasPredictiveAccess = useMemo(() => {
    const ALLOWED_PREDICTIVE_CLIENT_IDS = new Set([145, 149]);

    return profile.permissions.PER_CLIENT.some(
      (item) => ALLOWED_PREDICTIVE_CLIENT_IDS.has(item.clientId),
    );
  }, [profile.permissions.PER_CLIENT]);

  function canSeeDetailsByValue(value: number | undefined): boolean {
    if (hasPredictiveAccess) return true;

    return (value ?? 0) > 0;
  }

  function getUnitsIdsByVariant(variant: HealthVariant): string[] {
    return units
      .filter((unit) => unit.health === variant)
      .map((unit) => unit.id.toString());
  }

  function onClickDetails(variant: HealthVariant): void {
    if (hasPredictiveAccess) {
      history.push('/analise/preditivo');
      return;
    }

    const filter = handleFilterStateRoute(clients, selectedOptions, profile);
    const unitIds = getUnitsIdsByVariant(variant);

    if (unitIds.length) {
      filter.units = unitIds;
    }

    history.push(
      '/analise/unidades',
      filter,
    );
  }

  function onClickInoperativeDetails(): void {
    const filter = handleFilterStateRoute(clients, selectedOptions, profile);
    const unitIds = getUnitsIdsByVariant('inoperative');

    if (unitIds.length) {
      filter.units = unitIds;
    }

    history.push('/analise/unidades', filter);
  }

  useEffect(() => {
    if (hasHealth && !loading && clients && clients.length > 0) {
      const clientIds = clients.map(Number);
      refetch(clientIds, selectedOptions);
    }
  }, [
    clients,
    selectedOptions.healthStatus,
    selectedOptions.group,
    selectedOptions.state,
    selectedOptions.cities,
    selectedOptions.units,
    hasHealth,
    loading,
  ]);

  if (generalLoading) {
    return (<LoadingUnitsInfo />);
  }

  return (
    <HealthUnitsInfoContainer data-testId="health-units-info">
      <HealthUnitCard
        variant="healthy"
        onClickDetails={() => onClickDetails('healthy')}
        value={hasHealth && !loading ? data?.healthy : undefined}
        canSeeDetails={canSeeDetailsByValue(hasHealth && !loading ? data?.healthy : undefined)}
      />
      <HealthUnitCard
        variant="alert"
        onClickDetails={() => onClickDetails('alert')}
        value={hasHealth && !loading ? data?.alert : undefined}
        canSeeDetails={canSeeDetailsByValue(hasHealth && !loading ? data?.alert : undefined)}
      />
      <HealthUnitCard
        variant="critical"
        onClickDetails={() => onClickDetails('critical')}
        value={hasHealth && !loading ? data?.critical : undefined}
        canSeeDetails={canSeeDetailsByValue(hasHealth && !loading ? data?.critical : undefined)}
      />
      <HealthUnitCard
        variant="inoperative"
        onClickDetails={onClickInoperativeDetails}
        value={hasHealth && !loading ? data?.inoperative : undefined}
        canSeeDetails={canSeeDetailsByValue(hasHealth && !loading ? data?.inoperative : undefined)}
      />
    </HealthUnitsInfoContainer>
  );
}
